import type {
  CatalogItemInput,
  CatalogItemKind,
  CatalogPricingMode,
  CommercialRules,
  EventsCatalogItem,
  EventsSpace,
  SpaceInput,
} from './api/types';

export const CATALOG_KIND_LABEL: Record<CatalogItemKind, string> = {
  FOOD_BEVERAGE: 'Food and beverage',
  MENU: 'Menu',
  ROOM_RENTAL: 'Room rental',
  SPACE_RENTAL: 'Space rental',
  AV: 'Audio/visual',
  STAFFING: 'Staffing',
  BEVERAGE: 'Beverage',
  FEE: 'Fee',
  OTHER: 'Other',
};

export const PRICING_MODE_LABEL: Record<CatalogPricingMode, string> = {
  PER_PERSON: 'Per guest',
  FLAT: 'Flat',
  PER_HOUR: 'Per hour',
};

/** 30 → "30 min", 60 → "1 hr", 90 → "1 hr 30 min". */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} hr`;
  return `${hours} hr ${rest} min`;
}

/**
 * Extra schedule shown under the pricing mode. Default hourly items (60-minute
 * increments, no minimum) and plain flat items add nothing.
 */
export function catalogScheduleNote(
  item: Pick<
    EventsCatalogItem,
    | 'pricing_mode'
    | 'billing_increment_minutes'
    | 'minimum_minutes'
    | 'covered_minutes'
  >,
): string | null {
  if (item.pricing_mode === 'FLAT' && item.covered_minutes) {
    return formatDuration(item.covered_minutes);
  }
  if (item.pricing_mode !== 'PER_HOUR') return null;
  const { billing_increment_minutes: increment, minimum_minutes: minimum } =
    item;
  if (increment === 60 && minimum === 0) return null;
  if (minimum > 0) {
    return `${formatDuration(increment)} · ${formatDuration(minimum)} min`;
  }
  return formatDuration(increment);
}

/** "12.5" or "$1,200.00" → cents; null unless a non-negative amount with ≤2 decimals. */
export function parseDollars(text: string): number | null {
  const cleaned = text.trim().replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, fraction = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

export const centsToDollars = (cents: number) => (cents / 100).toFixed(2);

/** "8.25" (%) → 825 basis points; null unless 0–100 with ≤2 decimals. */
export function parsePercent(text: string): number | null {
  const cleaned = text.trim().replace(/%$/, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, fraction = ''] = cleaned.split('.');
  const bp = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return bp <= 10_000 ? bp : null;
}

export const bpToPercent = (bp: number) =>
  (bp / 100).toFixed(2).replace(/\.?0+$/, '');

function wholeNumber(text: string): number | null {
  const cleaned = text.trim();
  return /^\d+$/.test(cleaned) ? Number(cleaned) : null;
}

export type Validation<T> =
  | { ok: true; values: T }
  | { ok: false; errors: Record<string, string> };

const SPACE_CODE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ITEM_CODE = /^[-a-zA-Z0-9_]+$/;

export type SpaceDraft = {
  code: string;
  name: string;
  capacitySeated: string;
  capacityStanding: string;
  isCombinable: boolean;
  parentSpace: string;
  active: boolean;
};

export function spaceDraft(space?: EventsSpace): SpaceDraft {
  return {
    code: space?.code ?? '',
    name: space?.name ?? '',
    capacitySeated: String(space?.capacity_seated ?? 0),
    capacityStanding: String(space?.capacity_standing ?? 0),
    isCombinable: space?.is_combinable ?? false,
    parentSpace: space?.parent_space ? String(space.parent_space) : '',
    active: space?.active ?? true,
  };
}

/** Mirrors Space.clean(): lowercase-hyphen codes, no self-parenting. */
export function validateSpace(
  draft: SpaceDraft,
  spaceId: number | null,
  others: EventsSpace[],
): Validation<SpaceInput> {
  const errors: Record<string, string> = {};
  const code = draft.code.trim();
  if (!SPACE_CODE.test(code)) {
    errors.code =
      'Use lowercase letters, numbers and hyphens, like garden-room.';
  } else if (others.some((row) => row.code === code && row.id !== spaceId)) {
    errors.code = 'Another space at this venue uses that code.';
  }
  if (!draft.name.trim()) errors.name = 'Name the space.';
  const seated = wholeNumber(draft.capacitySeated);
  const standing = wholeNumber(draft.capacityStanding);
  if (seated === null) errors.capacitySeated = 'Enter a whole number.';
  if (standing === null) errors.capacityStanding = 'Enter a whole number.';
  const parent = draft.parentSpace ? Number(draft.parentSpace) : null;
  if (parent !== null && parent === spaceId) {
    errors.parentSpace = "A space can't be inside itself.";
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    values: {
      code,
      name: draft.name.trim(),
      capacity_seated: seated!,
      capacity_standing: standing!,
      is_combinable: draft.isCombinable,
      parent_space: parent,
      active: draft.active,
    },
  };
}

export type ItemDraft = {
  code: string;
  name: string;
  kind: string;
  pricingMode: CatalogPricingMode;
  price: string;
  billingIncrement: string;
  minimumMinutes: string;
  coveredMinutes: string;
  taxCategory: string;
  serviceFeeApplies: boolean;
  active: boolean;
};

export function itemDraft(item?: EventsCatalogItem): ItemDraft {
  return {
    code: item?.code ?? '',
    name: item?.name ?? '',
    kind: item?.kind ?? 'FOOD_BEVERAGE',
    pricingMode: item?.pricing_mode ?? 'PER_PERSON',
    price: item ? centsToDollars(item.unit_price_cents) : '',
    billingIncrement: String(item?.billing_increment_minutes ?? 60),
    minimumMinutes: String(item?.minimum_minutes ?? 0),
    coveredMinutes:
      item?.covered_minutes != null ? String(item.covered_minutes) : '',
    taxCategory: item?.tax_category ?? '',
    serviceFeeApplies: item?.service_fee_applies ?? true,
    active: item?.active ?? true,
  };
}

/**
 * The items a venue actually prices with: its own rows, plus shared
 * (organization) rows whose code the venue hasn't overridden. Mirrors the
 * backend's `effective_catalog_items`.
 */
export function effectiveCatalog(
  items: EventsCatalogItem[],
  venueId: number,
): EventsCatalogItem[] {
  const own = new Set(
    items.filter((row) => row.venue === venueId).map((row) => row.code),
  );
  return items.filter(
    (row) =>
      row.venue === venueId || (row.venue === null && !own.has(row.code)),
  );
}

/** A shared item replaced at this venue by a venue item with its code. */
export function isOverridden(
  item: EventsCatalogItem,
  items: EventsCatalogItem[],
  venueId: number,
): boolean {
  return (
    item.venue === null &&
    items.some((row) => row.venue === venueId && row.code === item.code)
  );
}

/** Active items' tax categories, which the pricing rules must keep. */
export function usedTaxCategories(
  items: EventsCatalogItem[],
  venueId: number,
): string[] {
  return Array.from(
    new Set(
      effectiveCatalog(items, venueId)
        .filter((row) => row.active)
        .map((row) => row.tax_category),
    ),
  );
}

/**
 * Codes are unique per venue. Reusing a shared item's code is allowed: the
 * venue item overrides it.
 */
export function validateItem(
  draft: ItemDraft,
  itemId: number | null,
  items: EventsCatalogItem[],
  taxCategories: string[],
  venueId: number,
): Validation<CatalogItemInput> {
  const errors: Record<string, string> = {};
  const code = draft.code.trim();
  if (!ITEM_CODE.test(code)) {
    errors.code = 'Use letters, numbers, hyphens and underscores.';
  } else if (
    items.some(
      (row) => row.venue === venueId && row.code === code && row.id !== itemId,
    )
  ) {
    errors.code = 'Another item at this venue uses that code.';
  }
  if (!draft.name.trim()) errors.name = 'Name the item.';
  const cents = parseDollars(draft.price);
  if (cents === null) errors.price = 'Enter a price like 85 or 85.50.';
  if (!taxCategories.includes(draft.taxCategory)) {
    errors.taxCategory = 'Choose a tax category from the pricing rules.';
  }
  let billingIncrement = 60;
  let minimumMinutes = 0;
  let coveredMinutes: number | null = null;
  if (draft.pricingMode === 'PER_HOUR') {
    const increment = wholeNumber(draft.billingIncrement);
    const minimum = wholeNumber(draft.minimumMinutes);
    if (increment === null || increment < 1) {
      errors.billingIncrement = 'Enter the billing increment in minutes.';
    } else {
      billingIncrement = increment;
    }
    if (minimum === null) {
      errors.minimumMinutes = 'Enter a minimum in minutes, or 0.';
    } else if (increment && minimum % increment !== 0) {
      errors.minimumMinutes =
        'Minimum must be a multiple of the billing increment.';
    } else {
      minimumMinutes = minimum ?? 0;
    }
    if (
      cents !== null &&
      increment !== null &&
      increment >= 1 &&
      (cents * increment) % 60 !== 0
    ) {
      errors.price =
        'Hourly price must divide evenly across the billing increment.';
    }
  } else if (draft.pricingMode === 'FLAT' && draft.coveredMinutes.trim()) {
    const covered = wholeNumber(draft.coveredMinutes);
    if (covered === null || covered < 1) {
      errors.coveredMinutes = 'Enter the minutes this price covers.';
    } else {
      coveredMinutes = covered;
    }
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    values: {
      code,
      name: draft.name.trim(),
      kind: draft.kind,
      pricing_mode: draft.pricingMode,
      unit_price_cents: cents!,
      billing_increment_minutes: billingIncrement,
      minimum_minutes: minimumMinutes,
      covered_minutes: coveredMinutes,
      tax_category: draft.taxCategory,
      service_fee_applies: draft.serviceFeeApplies,
      active: draft.active,
    },
  };
}

export type RulesDraft = {
  taxes: { key: string; category: string; rate: string }[];
  feeRate: string;
  feeTaxable: boolean;
  depositMode: 'percent' | 'flat';
  depositValue: string;
};

let taxKey = 0;
export const newTaxRow = (category = '', rate = '') => ({
  key: `tax-${(taxKey += 1)}`,
  category,
  rate,
});

export function rulesDraft(rules?: CommercialRules): RulesDraft {
  const deposit = rules?.deposit_rule;
  return {
    taxes: rules?.tax_rules.length
      ? rules.tax_rules.map((row) =>
          newTaxRow(row.category, bpToPercent(row.rate_bp)),
        )
      : [newTaxRow()],
    feeRate: rules ? bpToPercent(rules.service_fee.rate_bp) : '0',
    feeTaxable: rules?.service_fee.taxable ?? false,
    depositMode: deposit?.flat_cents != null ? 'flat' : 'percent',
    depositValue:
      deposit?.flat_cents != null
        ? centsToDollars(deposit.flat_cents)
        : deposit?.percent_bp != null
          ? bpToPercent(deposit.percent_bp)
          : '',
  };
}

/**
 * Mirrors CommercialRulesSerializer and UpdateCommercialRules: at least one
 * tax rule, unique categories, one deposit amount, and no removing a
 * category an active item still uses.
 */
export function validateRules(
  draft: RulesDraft,
  usedCategories: string[],
): Validation<CommercialRules> {
  const errors: Record<string, string> = {};
  const taxes: CommercialRules['tax_rules'] = [];
  const seen = new Set<string>();
  draft.taxes.forEach((row, index) => {
    const category = row.category.trim();
    const rate = parsePercent(row.rate);
    if (!category) errors[`tax-${index}`] = 'Name the tax category.';
    else if (seen.has(category)) {
      errors[`tax-${index}`] = `“${category}” is listed twice.`;
    } else if (rate === null) {
      errors[`tax-${index}`] = `Enter a rate for “${category}” like 8.25.`;
    }
    seen.add(category);
    if (category && rate !== null) taxes.push({ category, rate_bp: rate });
  });
  if (draft.taxes.length === 0) errors.taxes = 'Add at least one tax rule.';
  const missing = usedCategories.filter((category) => !seen.has(category));
  if (missing.length) {
    errors.taxes = `Active items still use ${missing
      .map((category) => `“${category}”`)
      .join(', ')}.`;
  }
  const fee = parsePercent(draft.feeRate);
  if (fee === null) errors.feeRate = 'Enter a percentage like 20.';
  const deposit =
    draft.depositMode === 'percent'
      ? parsePercent(draft.depositValue)
      : parseDollars(draft.depositValue);
  if (deposit === null) {
    errors.deposit =
      draft.depositMode === 'percent'
        ? 'Enter a percentage like 50.'
        : 'Enter an amount like 500.';
  } else if (draft.depositMode === 'percent' && deposit === 0) {
    errors.deposit =
      'A percentage deposit must be above 0. For no deposit, use a flat amount of 0.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    values: {
      tax_rules: taxes,
      service_fee: { rate_bp: fee!, taxable: draft.feeTaxable },
      deposit_rule: {
        percent_bp: draft.depositMode === 'percent' ? deposit : null,
        flat_cents: draft.depositMode === 'flat' ? deposit : null,
        due: 'ON_SIGNATURE',
      },
    },
  };
}
